
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';

import { Loader2, Edit, Camera } from 'lucide-react';
import { AvatarDisplay, avatarOptions } from '../utils/avatars';
import { calculateAge } from '../utils/zodiac';

import { supabase } from '../utils/supabaseClient';
import { useModal } from '../context/ModalContext';
import AvatarCropModal from '../components/profile/AvatarCropModal';
import { MBTI_TYPES, getMbtiStyle } from '../utils/mbti';
import { PERSONALITY_TRAITS, INTERESTS } from '../utils/profileConstants';
import { useIsDesktop } from '../hooks/useIsDesktop';
import MemberCenterLayout from '../components/common/MemberCenterLayout';
import MemberFormSaveButton from '../components/common/MemberFormSaveButton';

export default function Profile() {
  const navigate = useNavigate();
  const { currentUser, session, setCurrentUser, setSession } = useContext(UserContext);
  const [isEditing, setIsEditing] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState(null);
  const isDesktop = useIsDesktop();
  const { showAlert } = useModal();

  // Phase 1.96: 首次登入尚未完成初始設定（歡迎模式：無返回動線、儲存後導向首頁）
  const needsOnboarding = currentUser?.profile?.is_onboarded === false;

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      if (setSession) setSession(null);
      if (setCurrentUser) setCurrentUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('登出錯誤:', error);
    }
  };

  // 選完檔案先開啟裁切視窗，確認裁切後才上傳
  const handleAvatarFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showAlert('提示', '請選擇圖片檔案');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showAlert('提示', '圖片檔案不能超過 10MB');
      return;
    }

    setCropImageUrl(URL.createObjectURL(file));
  };

  const closeCropModal = () => {
    if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    setCropImageUrl(null);
  };

  const handleCropConfirm = async (croppedBlob) => {
    setIsUploadingAvatar(true);
    try {
      const filePath = `${session.user.id}/avatar.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, croppedBlob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // 加上 timestamp 避免瀏覽器快取顯示舊圖
      const urlWithTs = `${publicUrl}?t=${Date.now()}`;
      setProfileData(prev => ({ ...prev, avatar: urlWithTs }));
      closeCropModal();
      setShowAvatarSelector(false);
    } catch (error) {
      console.error('上傳頭像失敗:', error);
      showAlert('錯誤', '上傳失敗，請確認 Supabase Storage 的 avatars bucket 已建立並設為公開。');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      setProfileData(currentUser.profile);
    }
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  // 個人特質與興趣選項（集中定義於 utils/profileConstants.js，與探索頁篩選共用）
  const personalityTraits = PERSONALITY_TRAITS;
  const interests = INTERESTS;

  // 城市選項（台灣縣市 + 海外地區）
  const cityOptions = [
    // 台灣縣市
    '台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市',
    '基隆市', '新竹市', '嘉義市',
    '新竹縣', '苗栗縣', '彰化縣', '南投縣', '雲林縣', '嘉義縣',
    '屏東縣', '宜蘭縣', '花蓮縣', '台東縣',
    '澎湖縣', '金門縣', '連江縣',
    // 海外地區
    '海外地區'
  ];

  const handleTagToggle = (category, tag) => {
    setProfileData(prev => {
      const currentTags = prev[category] || [];
      const isSelected = currentTags.includes(tag);

      return {
        ...prev,
        [category]: isSelected
          ? currentTags.filter(t => t !== tag)
          : [...currentTags, tag]
      };
    });
  };

  const handleAvatarSelect = (avatarId) => {
    setProfileData(prev => ({
      ...prev,
      avatar: avatarId
    }));
  };

  const handleSave = async () => {
    if (!currentUser || !session || !profileData) return;

    if (!profileData.name?.trim()) {
      showAlert('提示', '請填寫姓名');
      return;
    }
    if (!profileData.birthday) {
      showAlert('提示', '請填寫生日，本服務僅供18歲以上人士使用。');
      return;
    }
    const age = calculateAge(profileData.birthday);
    if (age === null || age < 18) {
      showAlert('提示', '本服務僅供18歲以上人士使用。');
      return;
    }
    if (!profileData.gender) {
      showAlert('提示', '請選擇性別');
      return;
    }
    if (!profileData.city) {
      showAlert('提示', '請選擇所在城市');
      return;
    }
    if (!profileData.occupation?.trim()) {
      showAlert('提示', '請填寫職業');
      return;
    }
    if (!profileData.bio?.trim()) {
      showAlert('提示', '請填寫個人簡介');
      return;
    }

    setIsLoading(true);
    try {
      // Auto-compute age from birthday if birthday is set
      const dataToSave = { ...profileData };
      if (dataToSave.birthday) {
        const computedAge = calculateAge(dataToSave.birthday);
        if (computedAge !== null) {
          dataToSave.age = computedAge;
        }
      }

      delete dataToSave.created_at;
      delete dataToSave.last_active;

      // 通過必填與 18+ 驗證即視為完成初始設定
      dataToSave.is_onboarded = true;

      const { data, error } = await supabase
        .from('profiles')
        .update(dataToSave)
        .eq('id', currentUser.id)
        .select('*')
        .single();

      if (error) throw error;

      setCurrentUser({
        ...currentUser,
        profile: data
      });
      if (needsOnboarding) {
        showAlert('成功', '設定完成，歡迎加入 JoinU！開始揪朋友吧！');
        navigate('/home', { replace: true });
        return;
      }
      showAlert('成功', '個人資料已更新！');
      setIsEditing(false);
    } catch (error) {
      console.error("更新失敗:", error);
      showAlert('錯誤', '更新失敗，請重試');
    } finally {
      setIsLoading(false);
    }
  };

  if (!profileData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '1.2rem', marginLeft: '12px' }}>載入個人資料...</span>
      </div>
    );
  }

  const renderField = (label, name, value, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: '8px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontWeight: '500',
        color: '#2d3748',
        fontSize: '15px',
        letterSpacing: '0.02em'
      }}>{label}</label>
      {isEditing ? (
        <input
          type={type}
          name={name}
          value={value || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            fontSize: '15px',
            outline: 'none',
            transition: 'all 0.2s',
            backgroundColor: '#f8fafc',
            fontWeight: '400'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#4e8bca';
            e.target.style.backgroundColor = 'white';
            e.target.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.backgroundColor = '#f8fafc';
            e.target.style.boxShadow = 'none';
          }}
        />
      ) : (
        <p style={{
          background: '#f8fafc',
          padding: '14px 16px',
          borderRadius: '12px',
          border: '1px solid #f1f5f9',
          color: value ? '#2d3748' : '#9ca3af',
          fontSize: '15px'
        }}>{value || '未填寫'}</p>
      )}
    </div>
  );

  const renderSelectField = (label, name, value, options, placeholder = '請選擇') => (
    <div style={{ marginBottom: '8px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontWeight: '500',
        color: '#2d3748',
        fontSize: '15px',
        letterSpacing: '0.02em'
      }}>{label}</label>
      {isEditing ? (
        <select
          name={name}
          value={value || ''}
          onChange={handleInputChange}
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            fontSize: '15px',
            outline: 'none',
            backgroundColor: '#f8fafc',
            cursor: 'pointer',
            fontWeight: '400',
            transition: 'all 0.2s'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#4e8bca';
            e.target.style.backgroundColor = 'white';
            e.target.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.backgroundColor = '#f8fafc';
            e.target.style.boxShadow = 'none';
          }}
        >
          <option value="">{placeholder}</option>
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      ) : (
        <p style={{
          background: '#f8fafc',
          padding: '14px 16px',
          borderRadius: '12px',
          border: '1px solid #f1f5f9',
          color: value ? '#2d3748' : '#9ca3af',
          fontSize: '15px'
        }}>{value || '未填寫'}</p>
      )}
    </div>
  );



  const renderMbtiSection = () => {
    const selected = profileData.mbti || null;
    const selectedStyle = getMbtiStyle(selected);

    return (
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '12px',
          fontWeight: '500',
          color: '#2d3748',
          fontSize: '15px',
          letterSpacing: '0.02em'
        }}>MBTI 人格類型</label>
        {isEditing ? (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '8px'
            }}>
              {MBTI_TYPES.map(type => {
                const isSelected = selected === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setProfileData(prev => ({
                      ...prev,
                      mbti: prev.mbti === type ? null : type
                    }))}
                    style={{
                      padding: '10px 0',
                      borderRadius: '12px',
                      border: isSelected ? 'none' : '1px solid #e5e7eb',
                      background: isSelected ? '#4e8bca' : '#f3f4f6',
                      color: isSelected ? '#fff' : '#4b5563',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: isSelected ? '600' : '500',
                      letterSpacing: '0.05em',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.target.style.background = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.target.style.background = '#f3f4f6';
                    }}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #f1f5f9'
          }}>
            {selected && selectedStyle ? (
              <span style={{
                padding: '8px 14px',
                borderRadius: '16px',
                background: selectedStyle.bg,
                color: selectedStyle.color,
                fontSize: '14px',
                fontWeight: '600',
                letterSpacing: '0.05em'
              }}>
                {selected}
              </span>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '15px' }}>未選擇</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTagSection = (label, category, options) => {
    const selectedTags = profileData[category] || [];

    return (
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '12px',
          fontWeight: '500',
          color: '#2d3748',
          fontSize: '15px',
          letterSpacing: '0.02em'
        }}>{label}</label>
        {isEditing ? (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {options.map(tag => {
              const isSelected = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(category, tag)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: isSelected ? 'none' : '1px solid #e5e7eb',
                    background: isSelected ? '#4e8bca' : '#f3f4f6',
                    color: isSelected ? '#fff' : '#4b5563',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: isSelected ? '600' : '500',
                    transition: 'all 0.2s',
                    boxShadow: 'none',
                    letterSpacing: '0.01em'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.background = '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.background = '#f3f4f6';
                    }
                  }}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #f1f5f9',
            minHeight: '60px'
          }}>
            {selectedTags.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {selectedTags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '16px',
                      background: '#4e8bca',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '500',
                      boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2)',
                      letterSpacing: '0.02em'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '15px' }}>未選擇</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <MemberCenterLayout
      title={needsOnboarding ? '歡迎加入 JoinU！' : '我的個人資料'}
      subtitle={needsOnboarding ? '完成基本資料，就可以開始揪朋友了' : '維護與管理您的個人資訊'}
      activeKey="profile"
      topLevel
      hideSidebar={needsOnboarding}
      headerAction={needsOnboarding ? <button type="button" onClick={handleLogout} style={{ minHeight: '40px', padding: '8px 14px', color: '#376ba1', background: '#f6f9fc', border: '1px solid #dbe4ee', borderRadius: '12px', fontWeight: '750', cursor: 'pointer' }}>登出</button> : null}
    >

      <div style={{ maxWidth: '100%', margin: '0 auto', padding: 0 }}>
        <div style={{ maxWidth: '100%', margin: '0 auto' }}>

        <div style={{ padding: isEditing ? 0 : '0 0 12px', display: 'flex', justifyContent: 'flex-end' }}>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 20px',
                borderRadius: '12px',
                border: '2px solid #4e8bca',
                background: 'white',
                color: '#4e8bca',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f6f9fc';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Edit size={16} />
              編輯
            </button>
          )}
        </div>
      </div>
      <div style={{ maxWidth: '100%', margin: '0 auto', padding: 0 }}>
      <div>
      <div>

        {/* Card 0: 交友模式設定 */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 4px 0'
            }}>
              開啟交友模式
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
              開啟後可使用完整互動功能與揪活動。關閉時您的資料將隱藏，且只能使用揪運動。
            </p>
          </div>
          <div>
            {isEditing ? (
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                <input
                  type="checkbox"
                  name="is_social_enabled"
                  checked={profileData.is_social_enabled ?? true}
                  onChange={(e) => setProfileData(prev => ({ ...prev, is_social_enabled: e.target.checked }))}
                  style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                />
                <div style={{
                  width: '44px',
                  height: '24px',
                  backgroundColor: (profileData.is_social_enabled ?? true) ? '#4e8bca' : '#e5e7eb',
                  borderRadius: '12px',
                  position: 'relative',
                  transition: 'background-color 0.3s ease',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: (profileData.is_social_enabled ?? true) ? '22px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </label>
            ) : (
              <div style={{
                padding: '4px 12px',
                borderRadius: '999px',
                background: (profileData.is_social_enabled ?? true) ? '#ecfdf5' : '#fef2f2',
                color: (profileData.is_social_enabled ?? true) ? '#059669' : '#dc2626',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {(profileData.is_social_enabled ?? true) ? '已開啟' : '已關閉'}
              </div>
            )}
          </div>
        </div>

        {/* Card 1: 個人照片 */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px'
        }}>
          <h2 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            個人照片
          </h2>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* 当前选中的头像 - 点击可展开选择器 */}
            <button
              type="button"
              onClick={() => isEditing && setShowAvatarSelector(!showAvatarSelector)}
              style={{
                background: 'none',
                border: 'none',
                cursor: isEditing ? 'pointer' : 'default',
                padding: 0,
                position: 'relative'
              }}
            >
              <div style={{
                position: 'relative',
                display: 'inline-block'
              }}>
                <AvatarDisplay avatarId={profileData.avatar || 'avatar1'} size={100} />
                {isEditing && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#4e8bca',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}>
                    <Edit size={16} color="white" />
                  </div>
                )}
              </div>
            </button>

            {/* 創始會員徽章 */}
            {profileData.badge === 'founding' && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 14px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #eef4f9, #fde68a)',
                border: '1px solid #f59e0b',
                fontSize: '13px',
                fontWeight: '600',
                color: '#92400e'
              }}>
                🏅 創始會員
              </span>
            )}

            {/* 头像选择器 - 点击后展开 */}
            {isEditing && showAvatarSelector && (
              <div style={{
                width: '100%',
                maxWidth: '500px',
                animation: 'fadeIn 0.2s ease-in'
              }}>
                {/* 上傳個人照片 */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '2px dashed #4e8bca',
                    cursor: isUploadingAvatar ? 'not-allowed' : 'pointer',
                    backgroundColor: isUploadingAvatar ? '#f0faf9' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarFileSelect}
                      disabled={isUploadingAvatar}
                    />
                    {isUploadingAvatar ? (
                      <Loader2 size={16} color="#4e8bca" style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Camera size={16} color="#4e8bca" />
                    )}
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#4e8bca' }}>
                      {isUploadingAvatar ? '上傳中...' : '上傳個人照片'}
                    </span>
                  </label>
                </div>

                {/* 分隔線 */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'
                }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>或選擇預設頭像</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
                </div>

                {/* 頭像選擇 */}
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  marginBottom: '12px',
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  選擇頭像
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(8, 1fr)',
                  gap: '10px'
                }}>
                  {avatarOptions.map(avatar => (
                    <button
                      key={avatar.id}
                      type="button"
                      onClick={() => {
                        handleAvatarSelect(avatar.id);
                        setShowAvatarSelector(false);
                      }}
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: '50%',
                        border: profileData.avatar === avatar.id ? '3px solid #4e8bca' : '2px solid #e5e7eb',
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        padding: 0,
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <img
                        src={avatar.url}
                        alt={avatar.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
      <div>

        {/* Card 2: 基本資料 */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px'
        }}>
          <h2 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            基本資料
          </h2>

          <div style={isDesktop ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' } : undefined}>
            {renderField('姓名', 'name', profileData.name, 'text', '輸入你的名字')}

            {/* 出生日期 */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#2d3748',
                fontSize: '15px',
                letterSpacing: '0.02em'
              }}>出生日期</label>
              {isEditing ? (
                <div>
                  <input
                    type="date"
                    name="birthday"
                    value={profileData.birthday || ''}
                    onChange={handleInputChange}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      fontSize: '15px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      backgroundColor: '#f8fafc',
                      fontWeight: '400'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#4e8bca';
                      e.target.style.backgroundColor = 'white';
                      e.target.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.backgroundColor = '#f8fafc';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              ) : (
                <p style={{
                  background: '#f8fafc',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid #f1f5f9',
                  color: profileData.birthday ? '#2d3748' : '#9ca3af',
                  fontSize: '15px'
                }}>
                  {profileData.birthday || '未填寫'}
                </p>
              )}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '8px'
          }}>
            {renderSelectField('性別', 'gender', profileData.gender, ['男', '女', '其他'], '選擇性別')}
            {renderSelectField('所在城市', 'city', profileData.city, cityOptions, '台北')}
          </div>

          {renderField('職業', 'occupation', profileData.occupation, 'text', '你的職業')}
        </div>

        {/* Card 3: 個人偏好 */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px'
        }}>
          <h2 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            個人偏好
          </h2>


          {renderMbtiSection()}
          {renderTagSection('個人特質 (多選)', 'traits', personalityTraits)}
          {renderTagSection('興趣愛好 (多選)', 'interests', interests)}
        </div>



        {/* Card 5: 個人簡介 */}
        <div style={{
          background: 'white',
          padding: '16px 20px',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px'
        }}>
          <h2 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            個人簡介
          </h2>

          <div>
            {isEditing ? (
              <textarea
                name="bio"
                value={profileData.bio || ''}
                onChange={handleInputChange}
                placeholder="介紹一下自己吧..."
                style={{
                  width: '100%',
                  minHeight: '160px',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  backgroundColor: '#f8fafc',
                  fontWeight: '400',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4e8bca';
                  e.target.style.backgroundColor = 'white';
                  e.target.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
            ) : (
              <p style={{
                background: '#f8fafc',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid #f1f5f9',
                whiteSpace: 'pre-wrap',
                color: profileData.bio ? '#2d3748' : '#9ca3af',
                fontSize: '15px',
                lineHeight: '1.6',
                minHeight: '60px',
                margin: 0
              }}>{profileData.bio || '未填寫'}</p>
            )}
          </div>
        </div>

      </div>
      </div>

        {/* Save Button */}
        {isEditing && (
          <MemberFormSaveButton onClick={handleSave} isSaving={isLoading} />
        )}
      </div>
    </div>

    {/* 頭像裁切視窗 */}
    {cropImageUrl && (
      <AvatarCropModal
        imageUrl={cropImageUrl}
        isProcessing={isUploadingAvatar}
        onCancel={closeCropModal}
        onConfirm={handleCropConfirm}
      />
    )}
    </MemberCenterLayout>
  );
}
